import { type FC, type ReactNode } from "react";

import {
	type TypeInfoSelect,
	personDefaultValues,
} from "@moment/api-collab/differential/schema/index";
import { AddIcon, DeleteOutlineIcon } from "@moment/icons";

import { Button, IconButton } from "../../Button";
import { type ColorVariant, LEVEL_VARIANT_CLASSES, colorVariantOptions } from "../../Chip";
import { Input } from "../../Input";
import { InputCheckbox } from "../../InputCheckbox";
import { Label } from "../../Label";
import { Popover, PopoverContent, PopoverTrigger } from "../../Popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../Select";
import { Separator } from "../../Separator";
import { cn } from "../../utils";
import { type BaseViewHandle } from "../BaseViewHandle";
import { type HydratedColumnDef } from "../useHydratedColumnDefs";
import ColumnTypeExplanation from "./ColumnTypeExplanation";

const optionLabels = (typeInfo: TypeInfoSelect) => {
	return new Set(typeInfo.options?.map(({ label }) => label) ?? []);
};

const newOptionLabel = (typeInfo: TypeInfoSelect) => {
	const optionLabelsSet = optionLabels(typeInfo);
	let i = 0;
	let label = `Option ${i++}`;
	while (optionLabelsSet.has(label)) {
		label = `Option ${i++}`;
	}
	return label;
};

const OptionColor: FC<{ size?: "sm" | "md"; color: ColorVariant }> = ({ size = "md", color }) => {
	return (
		<div className="flex">
			<div
				className={cn(
					"tag-color-selection-container",
					"outline-2",
					"flex",
					"items-center",
					"justify-center",
					"rounded-full",
					{ "h-6": size === "md", "h-4": size === "sm" },
					{ "w-6": size === "md", "w-4": size === "sm" },
					"font-medium",
					LEVEL_VARIANT_CLASSES["secondary"][color]["background"],
					LEVEL_VARIANT_CLASSES["secondary"][color]["text"]
				)}
			>
				{/* TODO: label etc */}
				<div>A</div>
			</div>
		</div>
	);
};

const OptionColorChoice: FC<{
	color: ColorVariant;
	label: string;
	onClick?: (color: ColorVariant) => void;
	selected?: boolean;
}> = ({ color, label, onClick, selected }) => {
	return (
		<button
			data-selected={selected}
			// TODO: Replace this with `Tooltip` eventually. Tooltip shows on focus, while the
			// popover menu automatically focuses the first focusable element, and that can't be
			// turned off without managing focus manually, which is a lot of work. So, we have to
			// diable one or the other, and both give a hit to accessibility. Tab is used more than
			// screenreaders, so it wins here.
			title={label}
			className={cn("flex cursor-pointer rounded-full w-fit outline-2", {
				"outline outline-brand": selected,
				"outline outline-transparent hover:outline-brand-light focus-visible:outline-brand-light focus:outline-brand-light active:outline-brand-active":
					!selected,
			})}
			onClick={() => onClick?.(color)}
		>
			<OptionColor color={color} />
		</button>
	);
};

const ColumnDetails: FC<{
	column: HydratedColumnDef;
	viewHandle: BaseViewHandle;
}> = ({ column: { columnDef, displayOptions }, viewHandle }) => {
	let details: ReactNode = null;

	const readonly = displayOptions?.readonly ?? false;
	const readonlyCheckbox = (
		<div className="flex flex-col gap-2">
			<Label htmlFor="column-readonly-checkbox">Column Readonly</Label>
			<InputCheckbox
				id="column-readonly-checkbox"
				size="sm"
				isSelected={readonly}
				onChange={() =>
					viewHandle.updateColumnDisplayOptions(columnDef.name, { readonly: !readonly })
				}
			/>
		</div>
	);

	const typeInfo = columnDef.typeInfo;
	switch (typeInfo?.enrichedType) {
		case "Text":
			details = (
				<>
					<div className="flex flex-col gap-2">
						<Label>Default value</Label>
						<Input
							placeholder="Default value (optional)"
							defaultValue={typeInfo?.defaultValue}
							onBlur={(e) => {
								const defaultValue = e.target.value;
								viewHandle.updateColumn(columnDef.name, { defaultValue });
							}}
						/>
					</div>
					{readonlyCheckbox}
				</>
			);
			break;
		case "Reaction":
			details = readonlyCheckbox;
			break;
		case "Select": {
			details = (
				<>
					<div className="flex flex-col gap-2">
						<Label>Options</Label>
						{typeInfo.options?.map((option, index) => (
							<div
								key={`${option.label}-${option.color}`}
								className="flex gap-1 h-8 items-center"
							>
								<Popover>
									<PopoverTrigger asChild>
										<IconButton
											preset="quaternary"
											size="md"
											borderRadius="default"
											label="Select option color"
											icon={<OptionColor color={option.color} />}
										/>
									</PopoverTrigger>
									<PopoverContent className="!w-fit">
										<div className="grid grid-cols-4 gap-4">
											{colorVariantOptions.map((color) => (
												<OptionColorChoice
													key={color}
													color={color}
													label={color}
													selected={option.color === color}
													onClick={(color) => {
														const options =
															typeInfo.options?.map((o, i) =>
																i === index ? { ...o, color } : o
															) ?? [];
														viewHandle.updateColumn(columnDef.name, {
															typeInfo: { ...typeInfo, options },
														});
													}}
												/>
											))}
										</div>
									</PopoverContent>
								</Popover>
								<div className="flex grow flex-col">
									<Input
										size="md"
										defaultValue={option.label}
										onBlur={(e) => {
											const value = e.target.value;
											const optionLabelsSet = optionLabels(typeInfo);
											if (
												value === "" ||
												value === null ||
												optionLabelsSet.has(value)
											) {
												e.target.value = option.label;
												return;
											}
											const options =
												typeInfo.options?.map((o, i) =>
													i === index ? { ...o, label: value } : o
												) ?? [];
											viewHandle.updateColumn(columnDef.name, {
												typeInfo: { ...typeInfo, options },
											});
										}}
									/>
									{/* <TagLabel
										tagLabel={tag.label}
										onTagLabelChange={(tagLabel) =>
											updateTagLabel(tagIndex, tagLabel)
										}
									/> */}
								</div>
								<div className="ml-auto">
									<IconButton
										size="sm"
										preset="destructive"
										variant="text"
										elevation="flat"
										icon={<DeleteOutlineIcon />}
										label="Remove"
										onClick={() => {
											const options =
												typeInfo.options?.filter((_, i) => i !== index) ??
												[];
											viewHandle.updateColumn(columnDef.name, {
												typeInfo: { ...typeInfo, options },
											});
										}}
									/>
								</div>
							</div>
						))}
						<div>
							<Button
								size="sm"
								preset="quaternary"
								startIcon={<AddIcon />}
								onClick={() => {
									const options: { label: string; color?: ColorVariant }[] = [
										...(typeInfo.options ?? []),
										{ label: newOptionLabel(typeInfo), color: "default" },
									];
									viewHandle.updateColumn(columnDef.name, {
										typeInfo: { ...typeInfo, options },
									});
								}}
							>
								Add Option
							</Button>
						</div>
					</div>

					<Separator className="my-0" />

					<div className="flex flex-col gap-2">
						<Label>Default value</Label>
						<Select>
							<SelectTrigger
								disabled={!typeInfo.options || typeInfo.options?.length === 0}
							>
								<SelectValue placeholder="Select default value" />
							</SelectTrigger>
							<SelectContent>
								{typeInfo.options?.map(
									(option) =>
										option.label && (
											<SelectItem key={option.label} value={option.label}>
												<div className="flex gap-2 items-center">
													<OptionColor size="sm" color={option.color} />
													{option.label}
												</div>
											</SelectItem>
										)
								)}
							</SelectContent>
						</Select>
					</div>

					{readonlyCheckbox}
				</>
			);
			break;
		}
		case "Person":
			details = (
				<>
					<div className="flex flex-col gap-2">
						<Label>Default value</Label>
						<Select
							value={typeInfo.defaultValue}
							onValueChange={(value: "Empty" | "Current user") => {
								if (!personDefaultValues.includes(value)) {
									return;
								}
								viewHandle.updateColumn(columnDef.name, {
									typeInfo: { ...typeInfo, defaultValue: value },
								});
							}}
						>
							<SelectTrigger>
								<SelectValue placeholder="Select default value" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="Current user">Current user</SelectItem>
								<SelectItem value="Empty">Empty</SelectItem>
							</SelectContent>
						</Select>
					</div>
					{readonlyCheckbox}
				</>
			);
			break;
	}

	return (
		<div className="flex flex-col gap-4 -mt-0 -mb-4 -mx-4 pt-0 pb-4 px-4 max-h-72 overflow-y-auto">
			<ColumnTypeExplanation columnType={typeInfo?.enrichedType ?? "Text"} />
			<Separator className="my-0" />
			{details}
		</div>
	);
};

export default ColumnDetails;
